import { Monitor, FileText, Printer } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ViewMode } from '@/types/roadmap.types';

interface ViewModeToggleProps {
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  onPrintAllSlides?: () => void;
  onPrintDone?: () => void;
}

const modes: { value: ViewMode; icon: typeof Monitor; label: string }[] = [
  { value: 'presentation', icon: Monitor, label: 'Slides' },
  { value: 'report', icon: FileText, label: 'Report' },
  { value: 'print', icon: Printer, label: 'Print' },
];

function printWithOrientation(
  orientation: 'portrait' | 'landscape',
  onBeforePrint?: () => void,
  onAfterPrint?: () => void,
) {
  const styleId = '__print-orientation';
  let style = document.getElementById(styleId) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = `@media print { @page { size: letter ${orientation}; } }`;

  onBeforePrint?.();

  const handler = () => {
    onAfterPrint?.();
    window.removeEventListener('afterprint', handler);
  };
  window.addEventListener('afterprint', handler);

  // Allow React to re-render report layout and recharts to re-measure
  setTimeout(() => window.print(), 600);
}

export default function ViewModeToggle({
  mode,
  onModeChange,
  onPrintAllSlides,
  onPrintDone,
}: ViewModeToggleProps) {
  return (
    <div className="flex items-center bg-muted/60 rounded-lg p-0.5">
      {modes.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          onClick={() => {
            if (value === 'print') {
              const orientation = mode === 'presentation' ? 'landscape' : 'portrait';
              printWithOrientation(orientation, onPrintAllSlides, onPrintDone);
            } else {
              onModeChange(value);
            }
          }}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
            mode === value && value !== 'print'
              ? 'bg-white text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
          title={label}
        >
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}
